import { EventEmitter, Injectable } from "@angular/core";
import { ScrServiceManager } from "./servicemanager.service";
import { HttpClient } from "@angular/common/http";
import { ScrEnvironment } from "../../environments/environment";
import { WithCredNoCache } from "./authentication.service";
import { Observable, tap } from "rxjs";
import { IScrBaseResponse, ScrBaseResponse, ScrBaseService, ScrMessage } from "./base.service";
import { Account, Credit } from "../models/account.model";
import { AchPaymentMethod, Card } from "../models/payment.model";
import { Field, ObjectType, getColumns } from "../models/decorators";
import { BillingOrder } from "../models/billingorder.model";
import { MonthlyInvoice, MonthlyInvoiceLine } from "../models/monthlyinvoice.model";
import { ShortCode, ShortCodeDetails, SuspiciousActivity, SuspiciousActivityStatus, Transfer } from "../models/shortcodes.model";
import { User } from "../models/user.model";
import { Lease } from "../models/lease.model";

import { EventLog } from "../models/eventlog.model";
import { SystemMessage } from "../models/system-messages.model";
import { Promotion } from '../models/promotions.model';
import { PromoTemplateInput, PromoTemplateInputSubset, PromotionTemplate } from "../models/promotion-template.model";
import { AvailableResult, InternalActivity, ResMonDetails } from "../models/internalactivity.model";
import { RelatedCompany } from "../models/sd.relatedcompany.model";
import { CampaignType } from "../models/constants";
import { Partner, VetResults } from "../models/partner.model";
import { InterfaceActivity } from "../models/interfaceactivity.model";


export interface CreditMemoInfo {
  orderId: number;
  leases: (Lease & { amount?: number })[];
  events: EventLog[];
}


export interface CscCreditAmount {
  creditAmount: number;
  scId: number;
  selectType: string;
}

export interface CscCreditAmountList {
  orderId: number;
  creditReason: string;
  applicationType: string;
  creditInfo: CscCreditAmount[];
}

interface GqlOperation {
  operation?: string;
  query: string;
  variables?: object;
}

export interface GqlErrorExtensions {
  code?: string;
  field?: string;
  stacktrace?: string[];
}

export interface GqlErrorLocation {
  line: number;
  column: number;
}
export interface GqlError {
  message: string;
  locations?: GqlErrorLocation[];
  extensions?: GqlErrorExtensions;
}

/**
 * A GQL response extends ScrBaseResponse so that it can be processed
 * in the same way by the component error handling.   GQL errors get
 * promoted to be ScrMessage instances and application errors inside
 * the Gql data (D type) also get promoted.
 */
export interface GqlResponse<D> extends ScrBaseResponse {
  errors?: GqlError[];
  data?: D;
}

export class GqlBaseResponse extends ScrBaseResponse {
  errors?: GqlError[];
}


export type SearchQueryKey = keyof SearchQuery;

export class SearchResults {
  accounts?: Account[];

  billingOrder?: BillingOrder;
  monthlyInvoice?: MonthlyInvoice;
  shortCode?: ShortCodeDetails;
  brand?: Partner;

  limit?: string;
  page?: string;
  totalCount?: number;
}

export interface GqlSearchResults {
  sdaccountsearch: ScrBaseResponse & SearchResults;
}

export class SearchQuery {
  limit = 500;
  accountId?: number;
  orderMonthlyInvoice?: number;
  userId = '';
  company = '';
  shortCode = '';
  email = '';
  brandClient = '';
}


export class AccountDetails {
  account!: Account;
  cardsOnFile!: Card[];
  ach!: AchPaymentMethod[];
  unappliedCredits!: Credit[];
}

export interface GqlAccountDetailsResult {
  sdaccount: AccountDetails & ScrBaseResponse;
}


export interface GqlAccountResult {
  sdaccount: ScrBaseResponse & {
    account: Account
  };
}


export interface GqlUpdateAccountResult {
  sdUpdateAccount: ScrBaseResponse;
}

export interface GqlTransferListResult {
  sdTransferList: ScrBaseResponse & {
    transfers: Transfer[];
    totalCount: number;
    page: number;
    limit: number;
  };
}

export interface GqlTransferByTransferIdResult {
  sdTransferByTransferId: ScrBaseResponse & {
    transfers: Transfer[];
    totalCount: number;
    page: number;
    limit: number;
  };
}




export interface GqlUsersResult {
  sdusers: ScrBaseResponse & {
    users: User[];
  };
}


export interface GqlLeasesResult {
  sdLeasesByAccount: ScrBaseResponse & {
    leases: Lease[];
    totalCount: number;
    page: number;
    limit: number;
  };
}


export interface GqlOrdersResult {
  sdOrdersByAccount: ScrBaseResponse & {
    orders: BillingOrder[];
    totalCount: number;
    page: number;
    limit: number;
  };
}

export interface GqlInvoicesResult {
  sdInvoicesByAccount: ScrBaseResponse & {
    invoices: MonthlyInvoice[];
    totalCount: number;
    page: number;
    limit: number;
    unpaidCount: number;
  };
}

export interface GqlInvoiceResult {
  sdInvoice: ScrBaseResponse & {
    invoice?: MonthlyInvoice;
  };
}

export interface GqlShortCodeDetailsResult {
  sdShortCode: ScrBaseResponse & {
    latestLease: Lease;
    shortCode?: ShortCode
  }
}

export interface GqlLookupShortCodeResult {
  sdLookupShortCode: ScrBaseResponse & {
    shortCode: ShortCode,
    reservedBy?: Account[], // if reserved.
    lease?: Lease;
    order?: BillingOrder,
    allOrderIds?: number[];
  }
}


export type LeaseHistoryPlus = Lease & {
  paymentMethod: string;
  transferDate: Date;
};

export interface GqlShortCodeHistoryResult {
  sdShortCodeHistory: ScrBaseResponse & {
    scStatus: string,
    leases: LeaseHistoryPlus[];
  }
}


export interface GqlResendUserInviteResult {
  sdResendUserInvite: ScrBaseResponse;
}

export interface GqlDeletePendingUserResult {
  sdDeletePendingUser: ScrBaseResponse;
}

export interface GqlUpdateUserStatusResult {
  sdUpdateUserStatus: ScrBaseResponse;
}

export interface GqlCreateUserResult {
  sdCreateUser: ScrBaseResponse;
}
export interface GqlUpdateUserResult {
  sdUpdateUser: ScrBaseResponse;
}


export interface GqlCancelTransferResult {
  sdCancelTransfer: ScrBaseResponse;
}


export interface GqlCompleteTransferResult {
  sdCompleteTransfer: ScrBaseResponse & {
    transfer: Transfer;
  }
}


export interface GqlUnappliedCreditRefreshResult {
  sdRefreshUnappliedCredit: ScrBaseResponse & {
    unappliedCredits: Credit[]
  }
}

export interface GqlPrepareDeactivateMonthlyBillingResult {
  sdPrepareDeactivateMonthlyBilling: ScrBaseResponse & {
    paymentStatus: string,
    codeCount: number
  }
}

export interface GqlActivateMonthlyBillingResult {
  sdActivateMonthlyBilling: ScrBaseResponse;
}

export interface GqlDeactivateMonthlyBillingResult {
  sdDeactivateMonthlyBilling: ScrBaseResponse;
}
export interface GqlUpdatePoliticalCampaignResult {
  sdUpdatePoliticalCampaign: ScrBaseResponse;
}
export interface GqlOrderOrInvoiceResult {
  sdOrderOrInvoiceDetails: ScrBaseResponse & {
    leases?: Lease[];
    order?: BillingOrder;
    monthlyInvoice?: MonthlyInvoice;
  }
}

export interface GqlPrepareShortCodeCancelResult {
  sdPrepareShortCodeCancel: ScrBaseResponse & {
    text: string;
    title: string;
  }
}


export interface GqlRevertShortCodeCancelResult {
  sdRevertShortCodeCancel: ScrBaseResponse;
}



export interface GqlEventsResult {
  sdEventList: ScrBaseResponse & {
    events: EventLog[];
  }
}



export interface GqlPrepareShortCodeRollbackResult {
  sdPrepareShortCodeRollback: ScrBaseResponse & {
    latestLease: Lease
    previousLease: Lease
  }
}

export interface GqlShortCodeRollbackResult {
  sdShortCodeRollback: ScrBaseResponse;
}


export class ServiceDeskNavigationEvent {
  constructor(input?: Partial<ServiceDeskNavigationEvent>) {
    if (input) {
      if (input.accountId) this.accountId = input.accountId;
      if (input.orderId) this.orderId = input.orderId;
      if (input.invoiceId) this.invoiceId = input.invoiceId;
      if (input.scId) this.scId = input.scId;
      if (input.partnerId) this.partnerId = input.partnerId;
    }
  }
  accountId?: number;
  orderId?: number;
  invoiceId?: number;
  scId?: number;
  partnerId?: number;
}


export interface GqlLinkedAccountsResult {
  sdGetLinkedAccounts: ScrBaseResponse & {
    accounts: Account[];
    enabled: boolean;
    managingAccountId: number;
    managingAccountCompany: string;
  }
}
export interface GqlLinkAccountResult {
  sdLinkAccount: ScrBaseResponse;
}

export interface GqlUnlinkAccountResult {
  sdUnlinkAccount: ScrBaseResponse;
}

export interface GqlActivateLinkedAccountsResult {
  sdActivateLinkedAccounts: ScrBaseResponse;
}

export interface GqlDeactivateLinkedAccountsResult {
  sdDeactivateLinkedAccounts: ScrBaseResponse;
}


export interface GqlLeaseMbOptOutResult {
  sdLeaseMbOptOut: ScrBaseResponse;
}

export interface GqlLeasePrepareMbOptOutResult {
  sdLeasePrepareMbOptOut: ScrBaseResponse & {
    mbOptOutMessage: string;
  }
}



export interface GqlShortCodeCancelResult {
  sdShortCodeCancel: ScrBaseResponse;
}


export interface GqlShortCodeCancelResult {
  sdShortCodeCancel: ScrBaseResponse;
}


export interface GqlSetMaxWfpResult {
  sdSetMaxWfp: ScrBaseResponse;
}

export interface GqlUpdateCompanyNameResult {
  sdUpdateCompanyName: ScrBaseResponse;
}

export interface GqlUpdateInternalCommentsResult {
  sdUpdateInternalComments: ScrBaseResponse;
}

export interface GqlUpdateRegistryCommentsResult {
  sdUpdateRegistryComments: ScrBaseResponse;
}

export interface GqlUReviewProgramTypeResult {
  sdReviewProgramType: ScrBaseResponse;
}

export interface GqlUpdateMonthlyBillingAutoPayResult {
  sdUpdateMonthlyBillingAutoPay: ScrBaseResponse;
}

export interface GqlReleaseReservationsResult {
  sdReleaseReservations: ScrBaseResponse;
}

export interface GqlUnapplyOrderPaymentResult {
  sdUnapplyOrderPayment: ScrBaseResponse & {
    order: BillingOrder
  }
}

export interface GqlUnapplyMonthlyInvoicePaymentResult {
  sdUnapplyMonthlyInvoicePayment: ScrBaseResponse & {
    invoice: MonthlyInvoice;
  }
}

export interface GqlRegenerateInvoiceResult {
  sdRegenerateInvoice: ScrBaseResponse;
}

export interface GqlRegenerateReceiptResult {
  sdRegenerateReceipt: ScrBaseResponse;
}


export interface GqlPrepareCreditMemoInfoResult {
  sdPrepareCreditMemoInfo: ScrBaseResponse & CreditMemoInfo;
}


export interface CscCreditAmount {
  creditAmount: number;
  scId: number;
  selectType: string;
}

export interface CscCreditAmountList {
  orderId: number;
  creditReason: string;
  applicationType: string;
  creditInfo: CscCreditAmount[];
}

export interface GqlAddCreditMemoResult {
  sdAddCreditMemo: ScrBaseResponse;
}


export interface GqlLoginAsResult {
  sdLoginAs: ScrBaseResponse & {
    idToken: string;
  };
}



export interface GqlSystemMessagesResult {
  sdSystemMessages: ScrBaseResponse & {
    count: number;
    systemMessages: SystemMessage[];
  }
}

export interface GqlUpdateSystemMessageResult {
  sdUpdateSystemMessage: ScrBaseResponse;
}

export interface GqlDeleteSystemMessageResult {
  sdDeleteSystemMessage: ScrBaseResponse;
}

export interface GqlCreateSystemMessageResult {
  sdCreateSystemMessage: ScrBaseResponse;
}

export interface GqlSuspiciousActivityListResult {
  sdSuspiciousActivityList: ScrBaseResponse & {
    activities: SuspiciousActivity[];
  }
}

export interface GqlPromotionListResult {
  sdPromotionList: ScrBaseResponse & {
    promotions: Promotion[];
  }
}

export interface GqlPromotionTemplateListResult {
  sdPromotionTemplateList: ScrBaseResponse & {
    templates: PromotionTemplate<PromoTemplateInput>[];
  }
}

export interface GqlPromotionUpdateStateResult {
  sdUpdatePromotionState: ScrBaseResponse;
}


export interface GqlPromotionUpdateResult {
  sdUpdatePromotion: ScrBaseResponse & {
    promotionId: number;
  }
}

export interface GqlPromotionCreateResult {
  sdCreatePromotion: ScrBaseResponse & {
    promotionId: number;
  }
}



export interface GqlInternalActivityAckResult {
  sdAckInternalActivity: ScrBaseResponse & {
    activity: InternalActivity;
  }
}


export interface GqlInternalActivitiesResult {
  sdInternalActivityList: ScrBaseResponse & {
    total: number;
    activities: InternalActivity[];
  }
}


export interface GqlInterfaceActivitiesResult {
  sdInterfaceActivityList: ScrBaseResponse & {
    total: number;
    activities: InterfaceActivity[];
  }
}

export interface GqlInterfaceActivityResult {
  sdInterfaceActivity: ScrBaseResponse & {
    activity: InterfaceActivity;
  }
}


export interface GqlResMonStatusResult {
  sdResMonStatus: ScrBaseResponse & {
    results: InternalActivity[];
    availableResults: AvailableResult[];
  }
}

export interface UploadPartnersFile {
  status: string,
  messages: ScrMessage[];
  data: {
    uploadResponse: {
      downloadUrl: string;
    }
  }
}

@ObjectType()
export class TimerStat {
  @Field() key!: string;
  @Field() count!: number;
}

@ObjectType()
export class MasterTimerActivity {
  @Field() start!: string; // ISO datetime
  @Field() timer!: string;
  @Field() event!: string;
}

@ObjectType()
export class TimerActivity {
  @Field() start!: string; // ISO datetime
  @Field() end!: string;
  @Field() timer!: string;
  @Field() event!: string;
  @Field() duration!: string;
  @Field() result?: string;
  @Field() cwLogLink?: string;
  @Field() invocations?: string[];
}


export interface GqlTimerActivityResult {
  sdTimerActivity: ScrBaseResponse & {
    stats: TimerStat[];
    results: TimerActivity[];
    master: MasterTimerActivity[];
  }
}




@ObjectType()
export class ResourceMonitoringMonthlyResult {
  @Field() idx!: number;
  @Field() state!: number;
  @Field() activityIds!: number[];
  @Field() details!: ResMonDetails[];
}


export interface GqlResMonMonthlySummaryResult {
  sdResMonMonthlySummary: ScrBaseResponse & {
    results: ResourceMonitoringMonthlyResult[];
  }
}

export interface GqlResMonConfigResult {
  sdResMonGetConfig: ScrBaseResponse & {
    config: string;
  }
}

export interface GqlResMonConfigSaveResult {
  sdResMonSaveConfig: ScrBaseResponse;
}

export interface GqlRelatedCompanyListResult {
  sdRelatedCompanyList: ScrBaseResponse & {
    relatedCompanies: RelatedCompany[];
  }
}


export interface GqlUpdateRelatedCompanyResult {
  sdUpdateRelatedCompany: ScrBaseResponse & {
    relatedCompanyId: number;
  }
}
export interface GqlAddRelatedCompanyResult {
  sdAddRelatedCompany: ScrBaseResponse & {
    relatedCompanyId: number;
  }
}

export interface GqlDeleteRelatedCompanyResult {
  sdDeleteRelatedCompany: ScrBaseResponse & {
    relatedCompanies: RelatedCompany[];
  }
}

export interface GqlConfigResult {
  sdGetConfig: ScrBaseResponse & {
    config: string;
  }
}

export interface GqlUpdateConfigResult {
  sdUpdateConfig: ScrBaseResponse;
}

@ObjectType()
export class Banner {
  @Field() bannerId?: string;
  @Field() content!: string[];
  @Field() backgroundcolor?: string;
  @Field() fontcolor?: string;
  @Field() startDate?: string | Date;
  @Field() endDate?: string | Date;
  @Field() componentList!: string[];

  // local
  isExpansionDisplay?: boolean;
}


export interface GqlListBannersResult {
  sdListBanners: ScrBaseResponse & {
    banners: Banner[];
  }
}

export interface GqlDeleteBannerResult {
  sdDeleteBanner: ScrBaseResponse & {
    banners: Banner[];
  }
}

export interface GqlUpdateBannerResult {
  sdUpdateBanner: ScrBaseResponse & {
    banners: Banner[];
  }
}
export interface GqlCreateBannerResult {
  sdCreateBanner: ScrBaseResponse & {
    banners: Banner[];
  }
}



export interface GqlListPartnersResult {
  sdListPartners: ScrBaseResponse & {
    partners: Partner[];
  }
}

export interface GqlGetPartnerResult {
  sdGetPartner: ScrBaseResponse & {
    partner?: Partner;
    associatedScIds?: number[];
    scIdCount?: number;
  }
}
export interface GqlReleasePartnersResult {
  sdReleasePartners: ScrBaseResponse;
}

export interface GqlDeletePartnerResult {
  sdDeletePartner: ScrBaseResponse;
}

export interface GqlVetResultsResult {
  sdGetVetResults: ScrBaseResponse & {
    results?: VetResults;
  }
}



export interface GqlPrepareUpdateIntendedUsageResult {
  sdPrepareUpdateIntendedUsage: ScrBaseResponse & {
    numberOfCodes: number;
    intendedUsage: string;
    brands: Partner[];
    associatedBrands: number[];
  }
}

export interface GqlUpdateIntendedUsageResult {
  sdUpdateIntendedUsage: ScrBaseResponse;
}


@Injectable({
  providedIn: 'root'
})
export class GqlServiceDeskService extends ScrBaseService {

  /**
   * In the event some component changes the account data, this
   * event emitter will notify the subscriber that a change was made.
   */
  public sdNavigationEventEmitter = new EventEmitter<ServiceDeskNavigationEvent>();

  /**
   * In the event some component changes the account data, this
   * event emitter will notify the subscriber that a change was made.
   */
  public sdAccountUpdateEventEmitter = new EventEmitter<ServiceDeskNavigationEvent>();


  constructor(
    serviceManager: ScrServiceManager,
    private environment: ScrEnvironment,
    private http: HttpClient) {

    super(serviceManager);

  }


  /**
   * The current account has been updated and components may be holding
   * stale data.  If subscribed, this informs those components of the update.
   * @param accountId - id of account that was updated
   */
  public emitCurrentAccountUpdate(accountId: number): void {
    this.sdAccountUpdateEventEmitter.emit(new ServiceDeskNavigationEvent({ accountId }));
  }

  /**
  * The current account has been updated and components may be holding
  * stale data.  If subscribed, this informs those components of the update.
  * @param accountId - id of account that was updated
  */
  public emitNavigationEvent(e: ServiceDeskNavigationEvent): void {
    this.sdNavigationEventEmitter.emit(e);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  gqlQuery(o: any, usePhoneReplacer = false): string {

    const results = Object.entries(o).map(entry => {
      if ((typeof entry[1] === 'number') || typeof entry[1] === 'boolean') {
        return `${entry[0]}:${entry[1]}`;
      }
      else if (entry[1] === null || entry[1] === undefined) {
        return '';
      }
      else if (Array.isArray(entry[1])) {
        const v = entry[1].map(vv => {
          if ((typeof vv === 'number') || (typeof vv === 'boolean')) {
            return vv;
          }
          else if (typeof vv === 'object') {
            const v = ScrBaseService.phoneReplacer(entry[0], vv);
            if (typeof v === 'string') {
              return `"${vv}"`;
            }
            return `{ ${this.gqlQuery(vv, usePhoneReplacer)} } `;
          }
          else {
            return `"${vv}"`;
          }
        }).join();
        return `${entry[0]}:[${v}]`;
      }
      else if (typeof entry[1] === 'object') {
        const v = ScrBaseService.phoneReplacer(entry[0], entry[1]);
        if (typeof v === 'string') {
          return `${entry[0]}:"${v}"`;
        }
        return `${entry[0]}: { ${this.gqlQuery(entry[1], usePhoneReplacer)} } `;
      }
      else {
        return `${entry[0]}:"${entry[1].toString()}"`;
      }
    }).join(' ');

    return results;
  }

  accountSearch(options: Partial<SearchQuery>, accountColumns = 'summary'): Observable<GqlResponse<GqlSearchResults>> {

    const req =
    {
      query: `query GetAccounts {
      sdaccountsearch(req: {
        limit: 500 
        ${this.gqlQuery(options)}
      }) {
        messages { code, message }, status,
        accounts {
          ${getColumns(Account, accountColumns)} enabledUsers leasedCodes
                  },
        shortCode {
          scId, scStatus
        }
        billingOrder {
          orderId
        }
        monthlyInvoice {
          monthlyInvoiceId
        }
        brand {
          partnerId,
          partnerType
        }
        page, limit, totalCount,
      }
    }`,
      variables: {}
    };
    return this.gql<GqlSearchResults>(req, 'sdaccountsearch');
  }

  getAccount(accountId: number, accountColumns?: string): Observable<GqlResponse<GqlAccountResult>> {
    const query =
    {
      query: `query GetAccount($accountId: Int!) {
      sdaccount(accountId: $accountId) {
        messages { code, message }, status,
        account {
          ${getColumns(Account, accountColumns)}
        }
      }
    }`,
      variables: {
        accountId
      }
    };
    return this.gql<GqlAccountResult>(query, 'sdaccount');
  }

  getAccountDetails(accountId: number, accountColumns?: string): Observable<GqlResponse<GqlAccountDetailsResult>> {
    const query =
    {
      query: `query GetAccount($accountId: Int!) {
      sdaccount(accountId: $accountId) {
        messages { code, message }, status,
        unappliedCredits {
          ${getColumns(Credit)}
        },
        ach {
          ${getColumns(AchPaymentMethod)}
        },
        cardsOnFile {
          ${getColumns(Card)}
        },
        account {
          ${getColumns(Account, accountColumns)}
        }
      }
    }`,
      variables: {
        accountId
      }
    };
    return this.gql<GqlAccountDetailsResult>(query, 'sdaccount');
  }

  getAccountUsers(accountId: number): Observable<GqlResponse<GqlUsersResult>> {
    // get all columns for now.
    const query =
    {
      query: `query GetAccountUsers($accountId: Int!) {
        sdusers(accountId: $accountId) {
          messages { code, message }, status
          users { ${getColumns(User)} }
        }
      }`,
      variables: {
        accountId
      }
    };
    return this.gql<GqlUsersResult>(query, 'sdusers');
  }

  operation(operation: string, query: string, variables?: object): GqlOperation {
    const rv: GqlOperation = {
      operation,
      query: ScrBaseService.stringInject(query, [operation]),
      variables
    };
    return rv;
  }

  updateAccount(account: Partial<Account>, trackingNumber: string): Observable<GqlBaseResponse> {
    const query = this.operation('sdUpdateAccount',
      `mutation UpdateAccount($accountId: Int!, $trackingNumber: String) {
        {0} (req: {
           accountId: $accountId,
           trackingNumber: $trackingNumber,
           account: { ${this.gqlQuery(account, true)} }
        }) {
            status messages { message code }
        }
       }`,
      {
        trackingNumber, accountId: account.accountId!
      });

    return this.gql2(query);
  }


  getShortCodeList(accountId: number, page = 1, limit = 7500): Observable<GqlResponse<GqlLeasesResult>> {
    const query =
    {
      query: `query GetAccountLeases($accountId: Int!) {
        sdLeasesByAccount(accountId: $accountId page: ${page} limit: ${limit}) {
          messages { code, message }, status,
          leases { ${getColumns(Lease, 'sdlist')} }
        ,
        totalCount, page, limit
      }
      }`,
      variables: {
        accountId
      }
    };
    return this.gql<GqlLeasesResult>(query, 'sdLeasesByAccount');
  }

  getShortCodeLatestLease(scId: number, queryColumns?: string): Observable<GqlResponse<GqlShortCodeDetailsResult>> {
    const query =
    {
      query: `query GetShortCode($scId: Int!) {
        sdShortCode(scId: $scId) {
          messages { code, message }, status,
          latestLease { ${getColumns(Lease, queryColumns)} }
          shortCode { ${getColumns(ShortCode, queryColumns)} }
        }
      }`,
      variables: {
        scId
      }
    };
    return this.gql<GqlShortCodeDetailsResult>(query, 'sdShortCode');
  }

  lookupShortCode(scId: number, orderId?: number): Observable<GqlResponse<GqlLookupShortCodeResult>> {
    const query =
    {
      query: `query LookupShortCode {
        sdLookupShortCode(scId: ${scId} ${orderId ? `orderId: ${orderId}` : ''} ) {
          messages { code, message }, status,
          lease { 
            ${getColumns(Lease)} 
            aggregatorName 
            brands {
              partnerId, partnerType, company, fein, vetStatus, vetReportId, vetStatusUpdateTime
              vetResults {
                ${getColumns(VetResults)} 
              }
            }
            contentProvider {
             ${getColumns(Partner)} 
              vetResults {
                ${getColumns(VetResults)} 
              }
            }
          }
          shortCode { ${getColumns(ShortCode)} }
          order { ${getColumns(BillingOrder)} leases { scId } }
          reservedBy { accountId firstName lastName company email }
          allOrderIds
        }
      }`
    };
    return this.gql<GqlLookupShortCodeResult>(query, 'sdLookupShortCode');
  }

  shortCodeHistory(scId: number): Observable<GqlResponse<GqlShortCodeHistoryResult>> {
    const query =
    {
      query: `query ShortCodeHistory {
        sdShortCodeHistory(scId: ${scId} ) {
          messages { code, message }, status,
          scStatus,
          leases { ${getColumns(Lease)} paymentMethod, transferDate }
        }
      }`
    };
    return this.gql<GqlShortCodeHistoryResult>(query, 'sdShortCodeHistory');
  }




  static promoteMessages<T>(key: keyof T): (rsp: GqlResponse<T>) => void {
    return (rsp: GqlResponse<T>) => {
      const data = rsp.data as T;
      if (data && data[key]) {
        const details = data[key] as unknown as ScrBaseResponse;
        rsp.messages = details.messages;
        rsp.status = details.status;
      }
      if (rsp.errors) {
        if (!rsp.messages) {
          rsp.messages = [];
        }
        rsp.errors.forEach(error => {
          const code = error.extensions?.code ?? '';
          const field = error.extensions?.field;
          const m = new ScrMessage(code, error.message, field);
          rsp.messages.push(m);
        });
      }
    };
  }


  gql<T>(body: GqlOperation, key: keyof T, bypassApiGateway = false): Observable<GqlResponse<T>> {
    const url =
      bypassApiGateway ?
        `${this.environment.base}/fct/sd-gql/` :
        `${this.environment.base}/sd/graphql/`;

    //url = 'http://localhost:4000';

    // the function here moves top level gql errors and
    // embedded errors to where the application expects them.
    const fct = GqlServiceDeskService.promoteMessages<T>(key);
    return this.http.post<GqlResponse<T>>(url, body, WithCredNoCache).pipe(tap(fct));
  }

  /**
   * A new approach as work in progress.
   */
  gql2<T extends GqlBaseResponse>(op: GqlOperation, bypassApiGateway = false): Observable<T> {

    const url =
      bypassApiGateway ?
        `${this.environment.base}/fct/sd-gql/` :
        `${this.environment.base}/sd/graphql/`;

    return this.http.post<T>(url, {
      query: op.query,
      variables: op.variables
    }, WithCredNoCache).pipe(tap(rsp => {

      // convert GQL top level errors.
      if (rsp.errors) {
        if (!rsp.messages) {
          rsp.messages = [];
        }
        rsp.errors.forEach(error => {
          const code = error.extensions?.code ?? '';
          const field = error.extensions?.field;
          const m = new ScrMessage(code, error.message, field);
          rsp.messages.push(m);
        });
      }

      // convert application errors (inside data element);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const anyrsp = rsp as any;
      if (op.operation && anyrsp.data && anyrsp.data[op.operation]) {
        // there's some data there which should extend IScrBaseResponse.
        const appResp = anyrsp.data[op.operation] as IScrBaseResponse;
        if (appResp.status) {
          rsp.status = appResp.status;
        }
        if (appResp.messages) {
          if (!rsp.messages) {
            rsp.messages = appResp.messages;
          }
          else {
            appResp.messages.forEach(m => rsp.messages.push(m));
          }
        }
      }
    }));
  }


  /**
   * Resend the invitation email for a new user.
   * @param accountId - account ID
   * @param userId - user ID
   * @returns promise of response.
   */
  resendInvite(accountId: number, userId: number): Observable<GqlResponse<GqlResendUserInviteResult>> {
    const body = {
      query: `mutation ResendUserInvite($accountId: Int!, $userId: Int!) {
        sdResendUserInvite(accountId: $accountId, userId: $userId) {
          messages { code, message }, status
        }
      }`,
      variables: { accountId, userId }
    };
    return this.gql<GqlResendUserInviteResult>(body, 'sdResendUserInvite');
  }

  deletePendingUser(accountId: number, userId: number): Observable<GqlResponse<GqlDeletePendingUserResult>> {
    const body = {
      query: `mutation DeletePendingUser($accountId: Int!, $userId: Int!) {
        sdDeletePendingUser(accountId: $accountId, userId: $userId) {
          messages { code, message }, status
        }
      }`,
      variables: { accountId, userId }
    };
    return this.gql<GqlDeletePendingUserResult>(body, 'sdDeletePendingUser');
  }


  updateUserStatus(accountId: number, userName: string, disabled: boolean): Observable<GqlResponse<GqlUpdateUserStatusResult>> {
    const query =
    {
      query: `mutation UpdateUserStatus($accountId: Int!, $userName: String!, $disabled: Boolean) {
        sdUpdateUserStatus(accountId: $accountId, userName: $userName, disabled: $disabled) {
          messages { code, message }, status
        }
      }`,
      variables: {
        accountId,
        userName,
        disabled
      }
    };
    return this.gql<GqlUpdateUserStatusResult>(query, 'sdUpdateUserStatus');
  }

  createUser(user: User): Observable<GqlResponse<GqlCreateUserResult>> {
    const query =
    {
      query: `mutation CreateUser {
        sdCreateUser(req: {
          ${this.gqlQuery(user, true)}
        }) {
          messages { code, message }, status,
        }
      }`,
      variables: {
      }
    };
    return this.gql<GqlCreateUserResult>(query, 'sdCreateUser');
  }

  updateUser(user: User): Observable<GqlResponse<GqlUpdateUserResult>> {
    const query =
    {
      query: `mutation UpdateUser {
        sdUpdateUser(req: {
          ${this.gqlQuery(user, true)}
        }) {
          messages { code, message }, status,
        }
      }`,
      variables: {
      }
    };
    return this.gql<GqlUpdateUserResult>(query, 'sdUpdateUser');
  }


  getTransfers(accountId: number, page = 1, limit = 7500): Observable<GqlResponse<GqlTransferListResult>> {
    const query =
    {
      query: `query GetTransfers($accountId: Int!) {
        sdTransferList(accountId: $accountId page: ${page} limit: ${limit}) {
          messages { code, message }, status,
          transfers { ${getColumns(Transfer, 'sdlist')} },
          
          totalCount, page, limit
        }
      }`,
      variables: {
        accountId
      }
    };
    return this.gql<GqlTransferListResult>(query, 'sdTransferList');
  }


  getTransferById(transferId: number): Observable<GqlResponse<GqlTransferByTransferIdResult>> {
    const query =
    {
      query: `query sdTransferByTransferId($transferId: Int!) {
        sdTransferByTransferId(transferId: $transferId) {
          messages { code, message }, status,
          transfers { ${getColumns(Transfer, 'sdlist')} }
        }
      }`,
      variables: {
        transferId
      }
    };
    return this.gql<GqlTransferByTransferIdResult>(query, 'sdTransferByTransferId');
  }


  cancelTransfer(transferId: number, trackingNumber: string): Observable<GqlResponse<GqlCancelTransferResult>> {
    const query =
    {
      query: `mutation SdCancelTransfer($transferId: Int!, $trackingNumber: String!) {
        sdCancelTransfer(req: {
          transferId: $transferId,
          trackingNumber: $trackingNumber}) {
          messages { code, message }, status
        }
      }`,
      variables: {
        transferId,
        trackingNumber
      }
    };
    return this.gql<GqlCancelTransferResult>(query, 'sdCancelTransfer', true);
  }

  completeTransfer(transferId: number, transferType: string, trackingNumber: string): Observable<GqlResponse<GqlCompleteTransferResult>> {
    const query =
    {
      query: `mutation SdCompleteTransfer($transferId: Int!, $transferType: String!, $trackingNumber: String!) {
        sdCompleteTransfer(req: {
          transferId: $transferId,
          transferType: $transferType,
          trackingNumber: $trackingNumber}) {
          messages { code, message }, status,
          transfer { ${getColumns(Transfer, 'complete')} }
        }
      }`,
      variables: {
        transferId,
        transferType,
        trackingNumber
      }
    };
    return this.gql<GqlCompleteTransferResult>(query, 'sdCompleteTransfer', true);
  }




  refreshUnappliedCredit(accountId: number): Observable<GqlResponse<GqlUnappliedCreditRefreshResult>> {
    const query =
    {
      query: `mutation SdRefreshUnappliedCredits($accountId: Int!) {
        sdRefreshUnappliedCredit(accountId: $accountId) {
          messages { code, message }, status
          unappliedCredits {
            ${getColumns(Credit)}
          }
        }
      }`,
      variables: {
        accountId
      }
    };
    return this.gql<GqlUnappliedCreditRefreshResult>(query, 'sdRefreshUnappliedCredit', true);
  }






  prepareDeactivateMonthlyBilling(accountId: number): Observable<GqlResponse<GqlPrepareDeactivateMonthlyBillingResult>> {
    const query =
    {
      query: `mutation PrepareDeactivateMonthlyBilling($accountId: Int!) {
        sdPrepareDeactivateMonthlyBilling(
            accountId: $accountId) {
            status messages { message code }
            paymentStatus codeCount
        }
      }`,
      variables: {
        accountId
      }
    };
    return this.gql<GqlPrepareDeactivateMonthlyBillingResult>(query, 'sdPrepareDeactivateMonthlyBilling');
  }


  activateMonthlyBilling(accountId: number, autoPay: boolean): Observable<GqlResponse<GqlActivateMonthlyBillingResult>> {
    const query =
    {
      query: `mutation ActivateMonthlyBilling($accountId: Int!, $autoPay: Boolean!) {
        sdActivateMonthlyBilling(
            accountId: $accountId, 
            autoPay:$autoPay) {
            status messages { message code }
        }
    }`,
      variables: {
        accountId,
        autoPay
      }
    };
    return this.gql<GqlActivateMonthlyBillingResult>(query, 'sdActivateMonthlyBilling');
  }


  deactivateMonthlyBilling(accountId: number, trackingNumber: string, willBePaid: boolean): Observable<GqlResponse<GqlDeactivateMonthlyBillingResult>> {
    const query =
    {
      query: `mutation DeactivateMonthlyBilling($accountId: Int!, $willBePaid: Boolean!, $trackingNumber: String!) {
        sdDeactivateMonthlyBilling(req: {
            accountId: $accountId, 
            trackingNumber: $trackingNumber,
            willBePaid: $willBePaid
        }) {
            status messages { message code }
        }
    }`,
      variables: {
        accountId,
        willBePaid,
        trackingNumber
      }
    };
    return this.gql<GqlDeactivateMonthlyBillingResult>(query, 'sdDeactivateMonthlyBilling', true);
  }


  getOrderOrInvoiceDetails(orderId: number, columnSetName?: string, leases = false): Observable<GqlResponse<GqlOrderOrInvoiceResult>> {
    const query =
    {
      query: `query GetOrderOrMonthlyInvoiceDetails($orderId: Int!) {
        sdOrderOrInvoiceDetails(orderId: $orderId) {
            status messages { message code }
            order {
               ${getColumns(BillingOrder, columnSetName)} 
               ${leases ? 'leases {' + getColumns(Lease) + '}' : ''}
            }
        }
    }`,
      variables: {
        orderId
      }
    };
    return this.gql<GqlOrderOrInvoiceResult>(query, 'sdOrderOrInvoiceDetails');
  }


  prepareShortCodeCancel(scId: number): Observable<GqlResponse<GqlPrepareShortCodeCancelResult>> {
    const query =
    {
      query: `query PrepareShortCodeCancel($scId: Int!) {
        sdPrepareShortCodeCancel(scId: $scId) {
            status messages { message code }
            text title
        }
    }`, variables: { scId }
    };

    return this.gql<GqlPrepareShortCodeCancelResult>(query, 'sdPrepareShortCodeCancel');
  }


  prepareShortCodeRollback(scId: number, orderId: number): Observable<GqlResponse<GqlPrepareShortCodeRollbackResult>> {
    const query =
    {
      query: `query PrepareShortCodeRollback($scId: Int!, $orderId: Int!) {
        sdPrepareShortCodeRollback(scId: $scId, orderId: $orderId) {
            status messages { message code }
            latestLease { ${getColumns(Lease, 'summary')} }
            previousLease { ${getColumns(Lease, 'summary')} }
        }
    }`, variables: { scId, orderId }
    };

    return this.gql<GqlPrepareShortCodeRollbackResult>(query, 'sdPrepareShortCodeRollback');
  }


  shortCodeRollback(leaseId: number, comments: string, trackingNumber: string): Observable<GqlResponse<GqlShortCodeRollbackResult>> {
    const query = {
      query: `mutation ShortCodeRollback($leaseId: Int!, $comments: String!, $trackingNumber: String!) {
        sdShortCodeRollback(leaseId: $leaseId comments: $comments trackingNumber: $trackingNumber) {
          status messages { message code }
        }
    }`,
      variables: { leaseId, comments, trackingNumber }
    };

    return this.gql<GqlShortCodeRollbackResult>(query, 'sdShortCodeRollback', true);
  }



  updatePoliticalCampaign(scId: number, forPoliticalCampaign: boolean, politicalCampaignType: CampaignType | null, trackingNumber: string, politicalCampaignInfo: string | null, politicalCampaignStatus: string | null, politicalCampaignComments: string | null, fecId: string | null): Observable<GqlResponse<GqlUpdatePoliticalCampaignResult>> {
    const query = {
      query: `mutation UpdatePoliticalCampaign($scId: Int!, $fecId: String, $forPoliticalCampaign: Boolean!, $politicalCampaignType: String, $trackingNumber: String!, $politicalCampaignInfo: String, $politicalCampaignStatus: String!, $politicalCampaignComments: String) {
        sdUpdatePoliticalCampaign(req: {
            scId: $scId 
            fecId: $fecId
            politicalCampaignType: $politicalCampaignType
            politicalCampaignInfo: $politicalCampaignInfo
            politicalCampaignStatus: $politicalCampaignStatus
            politicalCampaignComments: $politicalCampaignComments
            forPoliticalCampaign: $forPoliticalCampaign
            trackingNumber: $trackingNumber
        }) {
            status messages { message code }
        }
    }`,
      variables: { scId, forPoliticalCampaign, politicalCampaignInfo, politicalCampaignType, politicalCampaignStatus, politicalCampaignComments, trackingNumber, fecId }
    };

    return this.gql<GqlUpdatePoliticalCampaignResult>(query, 'sdUpdatePoliticalCampaign');
  }


  revertShortCodeCancel(scId: number, trackingNumber?: string): Observable<GqlResponse<GqlRevertShortCodeCancelResult>> {
    const query = {
      query: `mutation RevertShortCodeCancel {
        sdRevertShortCodeCancel(req: {
            scId: ${scId} 
            ${trackingNumber ? 'trackingNumber: "' + trackingNumber + '"' : ''}
        }) {
            status messages { message code }
        }
    }`};

    return this.gql<GqlRevertShortCodeCancelResult>(query, 'sdRevertShortCodeCancel');
  }



  getOrdersByAccount(accountId: number, page = 1, limit = 7500): Observable<GqlResponse<GqlOrdersResult>> {
    const query =
    {
      query: `query GetOrdersByAccount {
        sdOrdersByAccount(accountId: ${accountId} page: ${page} limit: ${limit}) {
          messages { code, message }, status,
          orders { ${getColumns(BillingOrder, 'sdlist')} }
          ,
          totalCount, page, limit
        }
      }`
    };
    return this.gql<GqlOrdersResult>(query, 'sdOrdersByAccount');
  }



  public getEvents(accountId: number): Observable<GqlResponse<GqlEventsResult>> {
    const query =
    {
      query: `query GetEvents {
        sdEventList(accountId: ${accountId}) {
          messages { code, message }, status,
          events { ${getColumns(EventLog)} }
        }
      }`
    };
    return this.gql<GqlEventsResult>(query, 'sdEventList');
  }

  public getLinkedAccounts(accountId: number): Observable<GqlResponse<GqlLinkedAccountsResult>> {
    const query =
    {
      query: `query GetLinkedAccounts {
        sdGetLinkedAccounts(accountId: ${accountId}) {
          messages { code, message }, status,
          accounts { ${getColumns(Account)} }
          enabled  managingAccountId managingAccountCompany
        }
      }`
    };
    return this.gql<GqlLinkedAccountsResult>(query, 'sdGetLinkedAccounts');
  }

  public linkAccount(managerAccountId: number, linkedAccountId: number, linkedAccountName: string, trackingNumber: string): Observable<GqlResponse<GqlLinkAccountResult>> {

    const query = {
      query: `mutation LinkAccount {
        sdLinkAccount(req: {
          managerAccountId: ${managerAccountId}
          linkedAccountId: ${linkedAccountId}
          linkedAccountName: "${linkedAccountName}"
          trackingNumber: "${trackingNumber}"
        }) {
            status messages { message code }
        }
    }`};
    return this.gql<GqlLinkAccountResult>(query, 'sdLinkAccount');
  }

  public unlinkAccount(managerAccountId: number, linkedAccountId: number, trackingNumber: string, comments: string): Observable<GqlResponse<GqlUnlinkAccountResult>> {

    const query = {
      query: `mutation UnlinkAccount {
        sdUnlinkAccount(req: {
          managerAccountId: ${managerAccountId}
          linkedAccountId: ${linkedAccountId}
          comments: "${comments}"
          trackingNumber: "${trackingNumber}"
        }) {
            status messages { message code }
        }
    }`};
    return this.gql<GqlUnlinkAccountResult>(query, 'sdUnlinkAccount');
  }

  public activateLinkedAccounts(managerAccountId: number, trackingNumber: string): Observable<GqlResponse<GqlActivateLinkedAccountsResult>> {
    const query = {
      query: `mutation ActivateLinkedAccounts {
        sdActivateLinkedAccounts(req: {
          managerAccountId: ${managerAccountId}
          trackingNumber: "${trackingNumber}"
        }) {
            status messages { message code }
        }
    }`};
    return this.gql<GqlActivateLinkedAccountsResult>(query, 'sdActivateLinkedAccounts');
  }


  public deactivateLinkedAccounts(managerAccountId: number, trackingNumber: string): Observable<GqlResponse<GqlDeactivateLinkedAccountsResult>> {
    const query = {
      query: `mutation DeactivateLinkedAccounts {
        sdDeactivateLinkedAccounts(req: {
          managerAccountId: ${managerAccountId}
          trackingNumber: "${trackingNumber}"
        }) {
            status messages { message code }
        }
    }`};
    return this.gql<GqlDeactivateLinkedAccountsResult>(query, 'sdDeactivateLinkedAccounts');
  }



  getInvoicesByAccount(accountId: number, page = 1, limit = 5, detailLimit = 1): Observable<GqlResponse<GqlInvoicesResult>> {
    const query =
    {
      query: `query GetInvoicesByAccount {
        sdInvoicesByAccount(accountId: ${accountId} page: ${page} limit: ${limit} detailLimit: ${detailLimit}) {
          messages { code, message }, status,
          invoices {
             ${getColumns(MonthlyInvoice, 'sd')}
             lines { ${getColumns(MonthlyInvoiceLine, 'sd')} }
          },
          totalCount, page, limit, unpaidCount
        }
      }`
    };
    return this.gql<GqlInvoicesResult>(query, 'sdInvoicesByAccount');
  }

  getInvoice(invoiceId: number): Observable<GqlResponse<GqlInvoiceResult>> {
    const query =
    {
      query: `query GetInvoice {
        sdInvoice(invoiceId: ${invoiceId}) {
          messages { code, message }, status,
          invoice {
             ${getColumns(MonthlyInvoice, 'sd')}
             lines { ${getColumns(MonthlyInvoiceLine, 'sd')} }
          }
        }
      }`
    };
    return this.gql<GqlInvoiceResult>(query, 'sdInvoice');
  }

  public sdPrepareLeaseMbOptOut(scId: number): Observable<GqlResponse<GqlLeasePrepareMbOptOutResult>> {
    const query = {
      query: `query GetMbOptOut($scId: Int!) {
          sdLeasePrepareMbOptOut(scId: $scId) {
            messages { code, message }, status,
            mbOptOutMessage
          }
        }`, variables: { scId }
    };
    return this.gql<GqlLeasePrepareMbOptOutResult>(query, 'sdLeasePrepareMbOptOut');
  }


  public sdLeaseMbOptOut(leaseId: number, cancelSc: boolean, trackingNumber: string): Observable<GqlResponse<GqlLeaseMbOptOutResult>> {
    const query = {
      query: `mutation LeaseMbOptOut {
        sdLeaseMbOptOut(req: {
          leaseId: ${leaseId}
          cancelSc: ${cancelSc}
          trackingNumber: "${trackingNumber}"
        }) {
            status messages { message code }
        }
    }`};
    return this.gql<GqlLeaseMbOptOutResult>(query, 'sdLeaseMbOptOut', true);
  }



  public sdCancelShortCode(scId: number, cancelOption: string, trackingNumber: string, comments: string): Observable<GqlResponse<GqlShortCodeCancelResult>> {
    const query = {
      query: `mutation CancelShortCode {
        sdShortCodeCancel(req: {
          scId: ${scId}
          cancelOption: "${cancelOption}"
          trackingNumber: "${trackingNumber}"
          comments: "${comments}"
        }) {
            status messages { message code }
        }
    }`};
    return this.gql<GqlShortCodeCancelResult>(query, 'sdShortCodeCancel', true);
  }

  public revertSortCodeCancel(scId: number): Observable<GqlResponse<GqlShortCodeCancelResult>> {
    const query = {
      query: `mutation RevertShortCodeCancel {
        sdRevertShortCodeCancel(req: {
          scId: ${scId}
        }) {
            status messages { message code }
        }
    }`};
    return this.gql<GqlShortCodeCancelResult>(query, 'sdShortCodeCancel');
  }


  sdPrepareCreditMemoInfo(orderId: number): Observable<GqlResponse<GqlPrepareCreditMemoInfoResult>> {

    // get all columns for now.
    const query =
    {
      query: `query SdCreditMemoInfo {
        sdPrepareCreditMemoInfo(orderId: ${orderId}) {
          messages { code, message }, status
          orderId
          leases { ${getColumns(Lease)} }
          events { ${getColumns(EventLog)} }
        }
      }`
    };
    return this.gql<GqlPrepareCreditMemoInfoResult>(query, 'sdPrepareCreditMemoInfo');
  }

  public sdAddCreditMemo(creditAmountList: CscCreditAmountList, trackingNumber: string): Observable<GqlResponse<GqlAddCreditMemoResult>> {
    const query = {
      query: `mutation AddCreditMemo($trackingNumber: String!, $creditAmountList: CscCreditAmountList) {
        sdAddCreditMemo(req: {
          creditAmountList: $creditAmountList
          trackingNumber: $trackingNumber
        }) {
            status messages { message code }
        }
    }`,
      variables: {
        trackingNumber,
        creditAmountList
      }
    };
    return this.gql<GqlAddCreditMemoResult>(query, 'sdAddCreditMemo', true);
  }


  public updateMaxWfp(accountId: number, maxWfp?: number, restoreDefault?: boolean): Observable<GqlResponse<GqlSetMaxWfpResult>> {
    const query = {
      query: `mutation MaxWfp {
        sdSetMaxWfp(req: {
          accountId: ${accountId},
          ${restoreDefault ? `restoreDefault: ${restoreDefault}` : ''}
          ${maxWfp ? `maxWfp: ${maxWfp}` : ''}
        }) {
            status messages { message code }
        }
    }`};
    return this.gql<GqlSetMaxWfpResult>(query, 'sdSetMaxWfp');
  }

  public updateCompanyName(accountId: number, company: string, trackingNumber: string): Observable<GqlResponse<GqlUpdateCompanyNameResult>> {
    const query = {
      query: `mutation UpdateCompanyName($accountId: Int!, $company: String!, $trackingNumber: String!) {
        sdUpdateCompanyName(req: {
          accountId: $accountId,
          company: $company,
          trackingNumber: $trackingNumber
        }) {
            status messages { message code }
        }
    }`, variables: { accountId, company, trackingNumber }
    };

    return this.gql<GqlUpdateCompanyNameResult>(query, 'sdUpdateCompanyName');
  }

  public updateInternalComments(accountId: number, internalComments: string): Observable<GqlResponse<GqlUpdateInternalCommentsResult>> {
    const query = {
      query: `mutation UpdateInternalComments($req: SdUpdateInternalComments!) {
        sdUpdateInternalComments(req: $req) {
          status messages { message code }
        }
    }`, variables: {
      req: {
        accountId, internalComments
      }
    }};
    return this.gql<GqlUpdateInternalCommentsResult>(query, 'sdUpdateInternalComments');
  }

  public updateRegistryComments(accountId: number, registryComments: string): Observable<GqlResponse<GqlUpdateRegistryCommentsResult>> {
    const query = {
      query: `mutation UpdateRegistryComments($req: SdUpdateRegistryComments!) {
        sdUpdateRegistryComments(req: $req) {
          status messages { message code }
        }
    }`, variables: {
      req: {
        accountId, registryComments
      }
    }};
    return this.gql<GqlUpdateRegistryCommentsResult>(query, 'sdUpdateRegistryComments');
  }


  public reviewProgramType(accountId: number, programType: string, status: string, comments = ''): Observable<GqlResponse<GqlUReviewProgramTypeResult>> {
    const upd = 'sdReviewProgramType';
    const req = {
      accountId,
      programType,
      status,
      comments
    };
    const query = {
      query: `mutation ReviewProgramType($req: SdReviewProgramTypeRequest) {
        ${upd}(req: $req) {
          status messages { message code }
        }
    }`, variables: { req }
    };
    return this.gql<GqlUReviewProgramTypeResult>(query, upd);
  }

  public updateMonthlyBillingAutoPay(accountId: number, autoPay: boolean, trackingNumber: string): Observable<GqlResponse<GqlUpdateMonthlyBillingAutoPayResult>> {
    const upd = 'sdUpdateMonthlyBillingAutoPay';
    const query = {
      query: `mutation UpdateMonthlyBillingAutoPay {
        ${upd}(req: {
          accountId: ${accountId},
          monBillAutoPayEnabled: ${autoPay}
          trackingNumber: "${trackingNumber}"
        }) {
            status messages { message code }
        }
    }`};
    return this.gql<GqlUpdateMonthlyBillingAutoPayResult>(query, upd);
  }


  releaseReservations(scId: number, accountIds: number[]): Observable<GqlResponse<GqlReleaseReservationsResult>> {
    const upd = 'sdReleaseReservations';
    const query = {
      query: `mutation ReleaseReservations($accountIds: [Int!]!) {
        ${upd}(req: {
          accountIds: $accountIds
          scId: ${scId}
        }) {
            status messages { message code }
        }
    }`, variables: {
        accountIds
      }
    };
    return this.gql<GqlReleaseReservationsResult>(query, upd);
  }


  logInAsUser(userName: string, trackingNumber: string): Observable<GqlResponse<GqlLoginAsResult>> {
    const upd = 'sdLoginAs';
    const query = {
      query: `mutation LoginAs {
        ${upd}(req: {
          userName: "${userName}"
          trackingNumber: "${trackingNumber}"
        }) {
            status messages { message code }
            idToken
        }
    }`};
    return this.gql<GqlLoginAsResult>(query, upd);
  }

  unapplyOrderPayment(orderId: number, trackingNumber: string): Observable<GqlResponse<GqlUnapplyOrderPaymentResult>> {
    const upd = 'sdUnapplyOrderPayment';
    const query = {
      query: `mutation UnapplyOrderPayment {
        ${upd}(req: {
          orderId: ${orderId}
          trackingNumber: "${trackingNumber}"
        }) {
            status messages { message code }
            order { ${getColumns(BillingOrder)} }
        }
    }`};
    return this.gql<GqlUnapplyOrderPaymentResult>(query, upd, true);
  }

  unapplyMonthlyInvoicePayment(id: number, trackingNumber: string): Observable<GqlResponse<GqlUnapplyMonthlyInvoicePaymentResult>> {
    const upd = 'sdUnapplyMonthlyInvoicePayment';
    const query = {
      query: `mutation UnapplyMonthlyInvoicePayment {
        ${upd}(req: {
          monthlyInvoiceId: ${id}
          trackingNumber: "${trackingNumber}"
        }) {
            status messages { message code }
            invoice { ${getColumns(MonthlyInvoice)} }
        }
    }`};
    return this.gql<GqlUnapplyMonthlyInvoicePaymentResult>(query, upd, true);
  }



  regenerateInvoice(orderId: number, accountId: number, trackingNumber: string): Observable<GqlResponse<GqlRegenerateInvoiceResult>> {
    const upd = 'sdRegenerateInvoice';
    const query = {
      query: `mutation RegenerateInvoice {
        ${upd}(req: {
          orderId: ${orderId}
          accountId: ${accountId}
          trackingNumber: "${trackingNumber}"
        }) {
            status messages { message code }
        }
    }`};
    return this.gql<GqlRegenerateInvoiceResult>(query, upd);
  }


  regenerateReceipt(orderId: number, accountId: number, trackingNumber: string): Observable<GqlResponse<GqlRegenerateReceiptResult>> {
    const upd = 'sdRegenerateReceipt';
    const query = {
      query: `mutation RegenerateReceipt {
        ${upd}(req: {
          orderId: ${orderId}
          accountId: ${accountId}
          trackingNumber: "${trackingNumber}"
        }) {
            status messages { message code }
        }
    }`};
    return this.gql<GqlRegenerateReceiptResult>(query, upd);
  }





  // messages

  getSystemMessages(): Observable<GqlResponse<GqlSystemMessagesResult>> {
    const query =
    {
      query: `query GetSystemMessages {
        sdSystemMessages {
          messages { code, message }, status
          systemMessages { ${getColumns(SystemMessage)} }
          count
        }
      }`
    };
    return this.gql<GqlSystemMessagesResult>(query, 'sdSystemMessages');
  }


  deleteSystemMessage(messageId: number): Observable<GqlResponse<GqlDeleteSystemMessageResult>> {
    const upd = 'sdDeleteSystemMessage';
    const query = {
      query: `mutation DeleteSystemMessage {
        ${upd}(req: {
           messageId: ${messageId}
         }) {
            status messages { message code }
        }
    }`};
    return this.gql<GqlDeleteSystemMessageResult>(query, upd);
  }

  updateSystemMessage(messageId: number, messageType: string, text: string, subject: string): Observable<GqlResponse<GqlUpdateSystemMessageResult>> {
    const upd = 'sdUpdateSystemMessage';
    const query = {
      query: `mutation UpdateSystemMessage($messageId: Int, $messageType: String, $text: String, $subject: String) {
        ${upd}(req: { messageId: $messageId, messageType: $messageType, text: $text, subject: $subject }) {
          status messages { message code }
        }
    }`,
      variables: {
        messageId, messageType, text, subject
      }
    };
    return this.gql<GqlUpdateSystemMessageResult>(query, upd);
  }

  createSystemMessage(messageType: string, text: string, subject: string): Observable<GqlResponse<GqlCreateSystemMessageResult>> {
    const upd = 'sdCreateSystemMessage';
    const query = {
      query: `mutation CreateSystemMessage($messageType: String, $text: String, $subject: String) {
        ${upd}(req: { messageType: $messageType, text: $text, subject: $subject }) {
            status messages { message code }
        }
    }`,
      variables: {
        messageType, text, subject
      }
    };
    return this.gql<GqlCreateSystemMessageResult>(query, upd);
  }







  getSuspiciousActivity(): Observable<GqlResponse<GqlSuspiciousActivityListResult>> {
    const query =
    {
      query: `query GetSuspiciousActivities {
        sdSuspiciousActivityList {
          messages { code, message }, status
          activities { ${getColumns(SuspiciousActivity)} 
             statuses { ${getColumns(SuspiciousActivityStatus)}}
          }
        }
      }`
    };
    return this.gql<GqlSuspiciousActivityListResult>(query, 'sdSuspiciousActivityList');
  }





  // promotions

  getPromotions(): Observable<GqlResponse<GqlPromotionListResult>> {
    const key = 'sdPromotionList';
    const query =
    {
      query: `query GetPromotions {
        ${key} {
          messages { code, message }, status
          promotions { ${getColumns(Promotion)}  }
        }
      }`
    };
    return this.gql<GqlPromotionListResult>(query, key);
  }

  getPromotionTemplates(): Observable<GqlResponse<GqlPromotionTemplateListResult>> {
    const key = 'sdPromotionTemplateList';
    const query =
    {
      query: `query GetPromotionTemplates {
        ${key} {
          messages { code, message }, status
          templates { ${getColumns(PromotionTemplate)}  }
        }
      }`
    };
    return this.gql<GqlPromotionTemplateListResult>(query, key);
  }

  updatePromotionState(promotionId: number, active: boolean): Observable<GqlResponse<GqlPromotionUpdateStateResult>> {

    const upd = 'sdUpdatePromotionState';
    const query = {
      query: `mutation UpdatePromotionState($promotionId: Int, $active: Boolean) {
        ${upd}(req: { promotionId: $promotionId, active: $active }) {
            status messages { message code }
        }
    }`,
      variables: {
        promotionId, active
      }
    };
    return this.gql<GqlPromotionUpdateStateResult>(query, upd);

  }

  updatePromotion(promotionId: number, promo: PromotionTemplate<PromoTemplateInputSubset>): Observable<GqlResponse<GqlPromotionUpdateResult>> {

    const upd = 'sdUpdatePromotion';
    const query = {
      query: `mutation UpdatePromotion($promotionId: Int) {
        ${upd}(req: {
           promotionId: $promotionId, 
           promotion: { ${this.gqlQuery(promo)} }
        }) {
            status messages { message code }
            promotionId
          }
    }`,
      variables: {
        promotionId
      }
    };
    return this.gql<GqlPromotionUpdateResult>(query, upd);

  }


  createPromotion(promo: PromotionTemplate<PromoTemplateInputSubset>): Observable<GqlResponse<GqlPromotionCreateResult>> {

    const upd = 'sdCreatePromotion';
    const query = {
      query: `mutation CreatePromotion {
        ${upd}(req: {
           promotion: { ${this.gqlQuery(promo)} }
        }) {
            status messages { message code }
            promotionId
        }
    }`
    };
    return this.gql<GqlPromotionCreateResult>(query, upd);
  }






  getTimerActivity(date: string, clearCache: boolean): Observable<GqlResponse<GqlTimerActivityResult>> {
    const key = 'sdTimerActivity';
    const query =
    {
      query: `query GetTimerActivity($date: String!, $clearCache: Boolean!) {
        ${key}(date: $date, clearCache: $clearCache) {
          messages { code, message }, status
          stats { ${getColumns(TimerStat)} }
          master { ${getColumns(MasterTimerActivity)} }
          results { ${getColumns(TimerActivity)} }
        }
      }`,
      variables: {
        date, clearCache
      }
    };
    return this.gql<GqlTimerActivityResult>(query, key, true);
  }



  getInternalActivities(activityType: string, limit: number, page: number): Observable<GqlResponse<GqlInternalActivitiesResult>> {
    const key = 'sdInternalActivityList';
    const query =
    {
      query: `query GetInternalActivities($activityType: String!, $limit: Int, $page: Int) {
        ${key}(activityType: $activityType, limit: $limit, page: $page) {
          messages { code, message }, status
          activities { ${getColumns(InternalActivity)} }
          total
        }
      }`,
      variables: {
        activityType, limit, page
      }
    };
    return this.gql<GqlInternalActivitiesResult>(query, key);
  }

  initiateInternalActivity(activityType: string): Observable<GqlBaseResponse> {
    const query = this.operation('sdInitiateInternalActivity', `
      mutation InitiateInternalActivity($activityType: String!) {
        {0} (req: {
          activityType: $activityType
        }) {
          status messages { message code }
        }
    }`, { activityType });
    return this.gql2<GqlBaseResponse>(query);
  }


  ackInternalActivity(activityId: number, notes?: string): Observable<GqlResponse<GqlInternalActivityAckResult>> {

    const upd = 'sdAckInternalActivity';
    const query = {
      query: `mutation AckInternalActivity($activityId: Int!, $notes: String) {
        ${upd}(req: {
           activityId: $activityId, notes: $notes
        }) {
            status messages { message code }
            activity { ${getColumns(InternalActivity)} }
        }
    }`,
      variables: { activityId, notes }
    };
    return this.gql<GqlInternalActivityAckResult>(query, upd);
  }


  getResMonStatus(date?: Date): Observable<GqlResponse<GqlResMonStatusResult>> {

    const key = 'sdResMonStatus';
    const query =
    {
      query: `query GetResMonStatus($date: String) {
        ${key}(date: $date) {
          messages { code, message }, status
          results { ${getColumns(InternalActivity)} }
          availableResults { ${getColumns(AvailableResult)}}
        }
      }`,
      variables: { date }
    };
    return this.gql<GqlResMonStatusResult>(query, key);
  }

  getResMonMonthlySummary(date?: Date): Observable<GqlResponse<GqlResMonMonthlySummaryResult>> {

    const key = 'sdResMonMonthlySummary';
    const query =
    {
      query: `query GetResMonMonthlySummary($date: String) {
        ${key}(date: $date) {
          messages { code, message }, status
          results { ${getColumns(ResourceMonitoringMonthlyResult)} }
        }
      }`,
      variables: { date }
    };
    return this.gql<GqlResMonMonthlySummaryResult>(query, key);
  }

  getResMonConfig(): Observable<GqlResponse<GqlResMonConfigResult>> {

    const key = 'sdResMonGetConfig';
    const query =
    {
      query: `query SdResMonGetConfig {
        ${key} {
          messages { code, message }, status
          config
        }
      }`,
      variables: {}
    };
    return this.gql<GqlResMonConfigResult>(query, key);
  }

  saveResMonConfig(config: object): Observable<GqlResponse<GqlResMonConfigSaveResult>> {

    const key = 'sdResMonSaveConfig';
    const query =
    {
      query: `mutation SdResMonSaveConfig($config: JSON!) {
        ${key}(req: {
           config: $config
        }) {
          messages { code, message }, status
        }
      }`,
      variables: { config }
    };
    return this.gql<GqlResMonConfigSaveResult>(query, key);
  }




  getRelatedCompanies(): Observable<GqlResponse<GqlRelatedCompanyListResult>> {

    const key = 'sdRelatedCompanyList';
    const query =
    {
      query: `query GetRelatedCompanies {
        ${key} {
          messages { code, message }, status
          relatedCompanies { ${getColumns(RelatedCompany)} }
        }
      }`
    };
    return this.gql<GqlRelatedCompanyListResult>(query, key);
  }


  addRelatedCompany(relatedCompany: RelatedCompany): Observable<GqlResponse<GqlAddRelatedCompanyResult>> {

    const key = 'sdAddRelatedCompany';
    const req = { relatedCompany };

    const query =
    {
      query: `mutation AddRelatedCompany($req: SdRelatedCompanyRequest) {
        ${key}(req: $req) {
          messages { code, message }, status
          relatedCompanyId
        }
      }`, variables: { req }
    };
    return this.gql<GqlAddRelatedCompanyResult>(query, key);
  }

  updateRelatedCompany(relatedCompany: RelatedCompany): Observable<GqlResponse<GqlUpdateRelatedCompanyResult>> {

    const key = 'sdUpdateRelatedCompany';
    const req = { relatedCompany };
    const query =
    {
      query: `mutation UpdatedRelatedCompany($req: SdRelatedCompanyRequest) {
        ${key}(req: $req) {
          messages { code, message }, status
          relatedCompanyId
        }
      }`, variables: { req }
    };
    return this.gql<GqlUpdateRelatedCompanyResult>(query, key);
  }

  deleteRelatedCompany(relatedCompanyId: number): Observable<GqlResponse<GqlDeleteRelatedCompanyResult>> {

    const key = 'sdDeleteRelatedCompany';

    const query =
    {
      query: `mutation DeleteRelatedCompany($relatedCompanyId: Int!) {
        ${key}(relatedCompanyId: $relatedCompanyId) {
          messages { code, message }, status,
          relatedCompanies { ${getColumns(RelatedCompany)} }
        }
      }`, variables: { relatedCompanyId }
    };
    return this.gql<GqlDeleteRelatedCompanyResult>(query, key);
  }






  listBanners(): Observable<GqlResponse<GqlListBannersResult>> {

    const key = 'sdListBanners';
    const query =
    {
      query: `query ListBanners {
        ${key} {
          messages { code, message }, status
          banners { ${getColumns(Banner)} }
        }
      }`
    };
    return this.gql<GqlListBannersResult>(query, key);
  }

  createBanner(banner: Banner): Observable<GqlResponse<GqlCreateBannerResult>> {

    const key = 'sdCreateBanner';
    const req = { banner };

    const query =
    {
      query: `mutation CreateBanner($req: SdCreateBannerRequest) {
        ${key}(req: $req) {
          messages { code, message }, status
          banners { ${getColumns(Banner)} }
        }
      }`, variables: { req }
    };
    return this.gql<GqlCreateBannerResult>(query, key);
  }

  updateBanner(banner: Banner): Observable<GqlResponse<GqlUpdateBannerResult>> {

    const key = 'sdUpdateBanner';
    const req = { banner };

    const query =
    {
      query: `mutation UpdateBanner($req: SdUpdateBannerRequest) {
        ${key}(req: $req) {
          messages { code, message }, status
          banners { ${getColumns(Banner)} }
        }
      }`, variables: { req }
    };
    return this.gql<GqlUpdateBannerResult>(query, key);
  }

  deleteBanner(bannerId: string): Observable<GqlResponse<GqlDeleteBannerResult>> {

    const key = 'sdDeleteBanner';
    const req = { bannerId };

    const query =
    {
      query: `mutation DeleteBanner($req: SdDeleteBannerRequest) {
        ${key}(req: $req) {
          messages { code, message }, status
          banners { ${getColumns(Banner)} }
        }
      }`, variables: { req }
    };
    return this.gql<GqlDeleteBannerResult>(query, key);
  }


  getVetRequestDetails(vetReportId: number): Observable<GqlResponse<GqlVetResultsResult>> {

    const key = 'sdGetVetResults';
    const query =
    {
      query: `query GetVetResults($vetReportId: Int!) {
        ${key}(vetReportId: $vetReportId) {
          results {
            vetReportId, vetRequestId, vettedEntity, vetDate, findings errorCode errorDescription
          }
          messages { code, message }, status
        }
      }`, variables: { vetReportId }
    };

    return this.gql<GqlVetResultsResult>(query, key);
  }







  listPartners(accountId: number, partnerType: string, q?: string): Observable<GqlResponse<GqlListPartnersResult>> {
    const key = 'sdListPartners';
    const query =
    {
      query: `query ListPartners($accountId: Int!, $partnerType: String!) {
        ${key}(accountId: $accountId, partnerType: $partnerType) {
          messages { code, message }, status
          partners { ${getColumns(Partner, q)} }
        }
      }`, variables: { accountId, partnerType }
    };
    return this.gql<GqlListPartnersResult>(query, key);
  }

  getPartner(partnerId: number, q?: string): Observable<GqlResponse<GqlGetPartnerResult>> {

    const key = 'sdGetPartner';

    const query =
    {
      query: `query GetPartner($partnerId: Int!) {
        ${key}(partnerId: $partnerId) {
          messages { code, message }, status
          partner { ${getColumns(Partner, q)} }
          associatedScIds
          scIdCount
        }
      }`, variables: { partnerId }
    };
    return this.gql<GqlGetPartnerResult>(query, key);
  }


  getConfig(): Observable<GqlResponse<GqlConfigResult>> {

    const key = 'sdGetConfig';
    const query =
    {
      query: `query GetConfig {
        ${key} {
          messages { code, message }, status,
          config
        }
      }`
    };
    return this.gql<GqlConfigResult>(query, key);
  }

  updateConfig(cfg: string): Observable<GqlResponse<GqlUpdateConfigResult>> {

    const key = 'sdUpdateConfig';
    const query =
    {
      query: `mutation UpdateConfig($req: SdUpdateConfigRequest) {
        ${key}(req: $req) {
          messages { code, message }, status
        }
      }`, variables: {
        req: {
          config: cfg
        }
      }
    };
    return this.gql<GqlUpdateConfigResult>(query, key);
  }

  uploadPartnersCsv(formData: FormData): Observable<UploadPartnersFile> {
    const url = `${this.environment.base}/sd/partner/bulkUpload`;
    return this.http.post<UploadPartnersFile>(url, formData, WithCredNoCache).pipe(tap(rsp => {
      const downloadLink = rsp.data?.uploadResponse?.downloadUrl ?? rsp.data?.uploadResponse?.downloadUrl;
      if (downloadLink) {
        window.open(downloadLink);
      }
    }));
  }



  prepareUpdateIntendedUsage(leaseId: number): Observable<GqlResponse<GqlPrepareUpdateIntendedUsageResult>> {

    const key = 'sdPrepareUpdateIntendedUsage';
    const query =
    {
      query: `query PrepareUpdateIntendedUsage($leaseId: Int!) {
        ${key}(leaseId: $leaseId) {
          messages { code message } status
          numberOfCodes intendedUsage brands { company partnerId vetStatus }
          associatedBrands
        }
      }`, variables: {
        leaseId
      }
    };
    return this.gql<GqlPrepareUpdateIntendedUsageResult>(query, key);
  }


  releaseHeldPartners(accountId: number, partnerType: string): Observable<GqlResponse<GqlReleasePartnersResult>> {

    const key = 'sdReleasePartners';
    const query =
    {
      query: `query ReleasePartners($accountId: Int!, $partnerType: String!) {
        ${key}(accountId: $accountId, partnerType: $partnerType) {
          messages { code, message }, status
        }
      }`, variables: { accountId, partnerType }
    };
    return this.gql<GqlReleasePartnersResult>(query, key);
  }


  updateIntendedUsage(leaseId: number, intendedUsage: string, associatedBrands: number[], trackingNumber: string): Observable<GqlResponse<GqlUpdateIntendedUsageResult>> {

    const key = 'sdUpdateIntendedUsage';
    const query =
    {
      query: `mutation UpdateIntendedUsage($req: SdUpdateIntendedUsageRequest!) {
        ${key}(req: $req) {
          messages { code message } status
        }
      }`, variables: {
        req: {
          leaseId, intendedUsage, associatedBrands, trackingNumber
        }
      }
    };
    return this.gql<GqlUpdateIntendedUsageResult>(query, key);
  }




  getInterfaceActivities(accountId?: number, externalInterface?: string, interfaceName?: string, status?: string, limit = 20, page = 1): Observable<GqlResponse<GqlInterfaceActivitiesResult>> {
    const key = 'sdInterfaceActivityList';
    const query =
    {
      query: `query ListInterfaceActivities($accountId: Int, $externalInterface: String, $interfaceName: String, $status: String, $limit: Int, $page: Int) {
        ${key}(accountId: $accountId, externalInterface: $externalInterface, interfaceName: $interfaceName, status: $status, limit: $limit, page: $page) {
          messages { code, message }, status
          activities { ${getColumns(InterfaceActivity, 'sdlist')} }
          total
        }
      }`,
      variables: {
        accountId, externalInterface, interfaceName, status, limit, page
      }
    };
    return this.gql<GqlInterfaceActivitiesResult>(query, key);
  }



  getInterfaceActivity(requestId: number): Observable<GqlResponse<GqlInterfaceActivityResult>> {
    const key = 'sdInterfaceActivity';
    const query =
    {
      query: `query GetInterfaceActivity($requestId: Int!) {
        ${key}(requestId: $requestId) {
          messages { code, message }, status
          activity { ${getColumns(InterfaceActivity)} }
        }
      }`,
      variables: {
        requestId
      }
    };
    return this.gql<GqlInterfaceActivityResult>(query, key);
  }

  bulkAssociationUpload(formData: FormData): Observable<UploadPartnersFile> {
    const url = `${this.environment.base}/sd/partner/bulkAssociationUpload`;
    return this.http.post<UploadPartnersFile>(url, formData, WithCredNoCache);
  }
}

